import { lazy, useState } from "react"

import { useEntityAI } from "@/api/useInventory"
import { Switch } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { McpServerIcon, ReadOutlined } from "@/icons"
import { getMcpClientsCountAi } from "@/pages/DiscoveryPage/application/ai-discovery/useAiDiscovery.select"
import { AiComponentsTabTemplate } from "./AiComponentsTabTemplate"
import {
  MCP_CLIENTS_COMPONENT,
  TABS,
  toggleHighlightedClient,
  toggleHighlightedComponent
} from "./helpers"
import { AiComponentsTabs } from "./lazyComponents"
import { COMPONENT_TABS, DEFAULT_COMPONENT_TAB } from "./tabOptions"

import styles from "./AiComponentsTabAgentAiDiscoveryDrawer.module.scss"

import type { McpClientsAiResponse } from "@/api/useInventory.types"
import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"
import type { McpGraphState } from "./AiComponentsTabAgentAiDiscoveryDrawer.types"

const McpHierarchyGraph = lazy(() =>
  import("../HierarchyGraph/McpHierarchyGraph")
    .then((module) => ({ default: module.McpHierarchyGraph }))
    .catch(() => ({
      default: () => <div>Failed to load Hierarchy Graph</div>
    }))
)

const McpServersComponentsTab = lazy(() =>
  import("./McpServersComponentsTab")
    .then((module) => ({ default: module.McpServersComponentsTab }))
    .catch(() => ({
      default: () => <div>Failed to load MCP Servers Tab</div>
    }))
)

const INITIAL_MCP_GRAPH: McpGraphState = { show: false }
const SELECTABLE_TABS = [TABS.tools, TABS.knowledgeSources]

// Live data adds the MCP clients, a sub-tab and a toggle that draws them in the graph, and the
// knowledge sources sub-tab. Clicking a client, a tool or a knowledge source highlights it in the graph
export const LiveAiComponentsTab = (props: TabComponentProps) => {
  const [tab, setTab] = useState(DEFAULT_COMPONENT_TAB.agent)
  const [mcpGraph, setMcpGraph] = useState(INITIAL_MCP_GRAPH)
  const { t } = useTranslation("translation", { keyPrefix: "inventory.ai" })
  const recordId = props.record?.id
  // The clients listing's total greys out the MCP clients segment when the agent has none. It is
  // the same request the segment's tab makes, so it is cached, and it does not hold the drawer up
  const clientsCtx = useEntityAI<McpClientsAiResponse, number | undefined>(
    `${recordId}/components/${MCP_CLIENTS_COMPONENT}`,
    "agent",
    undefined,
    { select: getMcpClientsCountAi, enabled: !!recordId }
  )
  const noClients = clientsCtx.response === 0
  // The toggle shows in the card and in the graph's full view
  const mcpToggle = (
    <label className={styles.toggle}>
      <Switch
        size="small"
        className={styles.switch}
        checked={mcpGraph.show}
        onChange={(show) => setMcpGraph({ show })}
      />
      {t("mcpServers.showInGraph")}
    </label>
  )

  return (
    <AiComponentsTabTemplate
      tabProps={props}
      tab={tab}
      // The AI discovery tabs' order: agents, MCP servers, tools, models, then
      // knowledge sources
      options={[
        COMPONENT_TABS.agent[0],
        {
          label: t("mcpServers.title"),
          value: TABS.mcpServers,
          icon: <McpServerIcon />,
          disabled: noClients
        },
        ...COMPONENT_TABS.agent.slice(1),
        {
          label: t("knowledgeSources.title"),
          value: TABS.knowledgeSources,
          icon: <ReadOutlined />
        }
      ]}
      onTabChange={setTab}
      extra={mcpToggle}
      renderGraph={(id) => (
        <McpHierarchyGraph
          recordId={id}
          {...mcpGraph}
          fullViewExtra={mcpToggle}
        />
      )}
    >
      {tab === TABS.mcpServers ? (
        <McpServersComponentsTab
          recordId={recordId}
          highlightedClientId={mcpGraph.highlightedClientId}
          onClientClick={(clientId) =>
            setMcpGraph((state) => toggleHighlightedClient(state, clientId))
          }
        />
      ) : (
        <AiComponentsTabs
          recordId={recordId}
          tab={tab}
          selection={
            SELECTABLE_TABS.includes(tab)
              ? {
                  selectedId: mcpGraph.highlightedComponentId,
                  onSelect: (componentId) =>
                    setMcpGraph((state) =>
                      toggleHighlightedComponent(state, componentId)
                    )
                }
              : undefined
          }
        />
      )}
    </AiComponentsTabTemplate>
  )
}
